import { useEffect, useState } from "react";
// material-ui
import {
  Stack,
  Typography,
  DialogContent,
  DialogActions,
  Divider,
  Box,
  OutlinedInput,
  FormHelperText,
  InputLabel,
  Grid,
} from "@mui/material";

// third-party
import { useFormik } from "formik";
import * as Yup from "yup";
import { getDetails } from "use-places-autocomplete";
import useReadyPlacesAutocomplete from "hooks/useReadyPlacesAutocomplete";

// project imports
import Button from "components/@extended/LoadingButton";
import { useTranslation } from "utils/locales/utilityFunctions";
import useConfig from "hooks/useConfig";
import PlacesAutocomplete from "components/input/placesAutocomplete";
import { interpretPlaceDetails } from "lib/googleMaps/placesDataUtils";
import useStateRef from "react-usestateref";

// ==============================|| HOW DID YOU LEARN ABOUT US ||============================== //
export type BusinessRegNo = {
  businessNo: string;
  data: any;
  businessExists: boolean;
};

interface LearnAboutUsProps {
  businessRegNo: BusinessRegNo;
  disableBackButton?: boolean;
  userOnHoldBusinessExists?: boolean;
  setBusinessRegNo: (d: BusinessRegNo) => void;
  handleNext: () => Promise<void>;
  handleBack: () => void;
  setErrorIndex: (i: number | null) => void;
}

const CreateOrg = ({
  businessRegNo,
  disableBackButton,
  userOnHoldBusinessExists,
  setBusinessRegNo,
  handleNext,
  handleBack,
}: LearnAboutUsProps) => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { i18n } = useConfig();

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = useReadyPlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
      // componentRestrictions: {
      //    country: mapFromI18nToCountry(i18n),
      // }
      language: i18n,
    },
    debounce: 300,
  });

  // --- States and references --- //
  const [searchAddressSectionOpen, setSearchAddressSectionOpen] = useState(false);
  const [companyData, setCompanyData, companyDataRef] = useStateRef<any | null>(businessRegNo.data || null);

  // Form validation
  const formik = useFormik({
    initialValues: {
      businessNo: businessRegNo.businessNo,
      businessName: businessRegNo.data?.name || "",
    },
    validationSchema: Yup.object().shape({
      businessNo: Yup.string(),
      businessName: Yup.string().max(255).required(translate("business-name-required")),
    }),
    onSubmit: async (values) => {
      const businessNo = values.businessNo;
      setBusinessRegNo({
        businessNo: businessNo,
        data: { ...companyDataRef.current, name: values.businessName },
        businessExists: false,
      });
      await handleNext();
    },
  });

  useEffect(() => {
    if (!formik.dirty && !formik.isSubmitting && businessRegNo.data?.name) {
      setCompanyData(businessRegNo.data);
      void formik.setValues({ businessNo: businessRegNo.businessNo, businessName: businessRegNo.data.name });
    }
  }, [businessRegNo.businessNo, businessRegNo.data?.name]);

  const handleInput = (e: any) => {
    setValue(e.target.value);
  };

  const handleSelect = (event: any) => {
    formik.setFieldValue("businessName", "");
    formik.setFieldValue("businessNo", "");
    setValue("", false);
    clearSuggestions();
    getDetails({ placeId: event.place_id }).then((details) => {
      const data = interpretPlaceDetails(details);
      if (data.businessName) {
        formik.setFieldValue("businessName", data.businessName);
      }
      if (data.address) {
        const { streetName, streetNumber, locality, postalTown, postalCode, country } =
          data.address;
        const companyData = {
          address: "",
          city: "",
          zipcode: "",
          country: "",
        };
        if (streetName) {
          if (streetNumber) {
            companyData["address"] = streetName + " " + streetNumber;
          } else {
            companyData["address"] = streetName;
          }
        }
        if (locality) {
          companyData["city"] = locality;
        } else if (postalTown) {
          companyData["city"] = postalTown;
        }
        if (postalCode) companyData["zipcode"] = postalCode;
        if (country) companyData["country"] = country;

        setCompanyData({
          ...companyData,
        });
      }
    });
  };

  // If the user is on hold
  if (userOnHoldBusinessExists) {
    return (
      <DialogContent>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {translate("onboarding-company-title")}
        </Typography>

        <Typography variant="h6" sx={{ mb: 3 }}>
          {translate("onboarding-on-hold-business-exist-text")}
        </Typography>
      </DialogContent>
    );
  }

  const companyAddressRender = (
    formik: any,
    settings?: { withCompanyName: boolean; withBusinessReg: boolean }
  ) => {
    if (!companyDataRef.current) return null;
    const { address, zipcode, city, country } = companyDataRef.current;
    // Check settings
    let withCompanyName = true;
    let withBusinessReg = true;
    if (settings) {
      if (settings.withCompanyName !== undefined) withCompanyName = settings.withCompanyName;
      if (settings.withBusinessReg !== undefined) withBusinessReg = settings.withBusinessReg;
    }
    // Form company address
    let companyAddress = [];
    if (address) companyAddress.push(address);
    if (zipcode) companyAddress.push(zipcode);
    if (city) companyAddress.push(city);
    if (country) companyAddress.push(country);

    const { values, touched, errors, handleBlur, handleChange } = formik;
    return (
      <Grid container rowSpacing={3}>
        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h5">
            {translate("business-details-onboarding")}
          </Typography>
        </Grid>

        <Grid item sm={6} xs={12}>
          <Stack spacing={2} sx={{ mt: -1 }}>
            {withCompanyName && (
              <Stack spacing={1}>
                <InputLabel htmlFor="business-name-onboarding">
                  {translate("onboarding-business-name")} *
                </InputLabel>

                <OutlinedInput
                  id="business-name-onboarding"
                  value={values.businessName}
                  name="businessName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={translate("business-name-placeholder")}
                  fullWidth
                  error={Boolean(touched.businessName && errors.businessName)}
                  color="secondary"
                />

                {touched.businessName && errors.businessName && (
                  <FormHelperText error id="helper-text-business-name-onboarding">
                    {errors.businessName}
                  </FormHelperText>
                )}
              </Stack>
            )}

            {withBusinessReg && (
              <Stack spacing={1}>
                <InputLabel htmlFor="business-number-onboarding">
                  {translate("business-number")}
                </InputLabel>

                <OutlinedInput
                  id="business-number-onboarding"
                  value={values.businessNo}
                  name="businessNo"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder={translate("business-number-placeholder")}
                  fullWidth
                  error={Boolean(touched.businessNo && errors.businessNo)}
                  color="secondary"
                />

                {touched.businessNo && errors.businessNo && (
                  <FormHelperText error id="helper-text-business-number-onboarding">
                    {errors.businessNo}
                  </FormHelperText>
                )}
              </Stack>
            )}
          </Stack>
        </Grid>

        <Grid item sm={6} xs={12}>
          <Box sx={{ borderRadius: 1, bgcolor: "secondary.lighter", p: 2, ml: { sm: 2, xs: 0 } }}>
            <Stack spacing={0.5}>
              <Typography sx={{ fontWeight: 500 }}>{translate("onboarding-company-location")}</Typography>

              <Typography sx={{ whiteSpace: "pre-wrap" }} variant="h6">
                {companyAddress.join("\n")}
              </Typography>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const noBusinessNumberSearchContent = () => {
    if (!searchAddressSectionOpen) {
      setSearchAddressSectionOpen(true);
    }
    return (
      <Stack spacing={0.5}>
        {searchAddressContent()}
        
        {companyAddressRender(formik)}
      </Stack>
    );
  };

  const searchAddressContent = () => {
    return (
      <>
        <PlacesAutocomplete
          value={value}
          placeholder={translate("search-bar-placeholder-text")}
          onChange={handleInput}
          onSelect={handleSelect}
          disabled={!ready}
          fullWidth={true}
          data={data}
          status={status}
          googleMaps
        />
      </>
    );
  };

  // Else return normally
  // Check for disabled next button
  let disableNextButton = false;
  if (!searchAddressSectionOpen) {
    if (formik.values.businessNo === "" || userOnHoldBusinessExists) {
      disableNextButton = true;
    }
  } else {
    if (formik.values.businessName === "") {
      disableNextButton = true;
    }
  }

  return (
    <>
      <DialogContent>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {translate("onboarding-company-title")}
        </Typography>

        <Typography variant="h6" sx={{ mb: 2 }}>
          {translate("onboarding-company-text")}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <form onSubmit={formik.handleSubmit} id="validation-forms">
            {noBusinessNumberSearchContent()}
          </form>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions>
        {/* ACTION */}
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          sx={{ width: 1, pl: 1.5, pr: 1.5, py: 0.75 }}
        >
          <Button
            color="secondary"
            disabled={disableBackButton || userOnHoldBusinessExists}
            onClick={handleBack}
          >
            {translate("back")}
          </Button>

          <Button
            variant="contained"
            type="button"
            disabled={disableNextButton || formik.isSubmitting}
            loading={formik.isSubmitting}
            onClick={() => {
              formik.handleSubmit();
            }}
            sx={{
              bgcolor: "primary.light",
              "&.Mui-disabled": {
                bgcolor: "secondary.lighter",
                color: "secondary.main",
              },
            }}
          >
            {translate("next")}
          </Button>
        </Stack>
      </DialogActions>
    </>
  );
};

export default CreateOrg;
